import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import SEO from "@/components/SEO";
import RealisationCard from "@/components/RealisationCard";

const API = process.env.REACT_APP_BACKEND_URL;
const slugify = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function RealisationsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/property/realisations`).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const byCity = useMemo(() => {
    const groups = {};
    items.forEach((it) => { (groups[it.city] = groups[it.city] || []).push(it); });
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [items]);

  return (
    <div className="min-h-screen bg-[#EDEBE4]" data-testid="realisations-page">
      <SEO title="Nos réalisations nettoyage — Avant / Après | ALTURA Property Services"
        description="Découvrez nos interventions de nettoyage automobile, mobilier et logements en Yvelines et Île-de-France, classées par ville, en photos avant / après."
        path="/property-services/realisations" />
      <section className="bg-[#1A1A1A] pt-32 pb-16 sm:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-3">Avant / Après</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">Nos réalisations</h1>
          <p className="text-white/60 text-sm sm:text-base max-w-xl">Des interventions réelles, classées par ville. Faites glisser le curseur pour comparer.</p>
          {byCity.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8" data-testid="realisations-city-nav">
              {byCity.map(([city, list]) => (
                <a key={city} href={`#${slugify(city)}`} data-testid={`city-link-${slugify(city)}`}
                  className="flex items-center gap-1.5 border border-white/20 text-white/80 px-3 py-1.5 text-xs hover:bg-white hover:text-[#1A1A1A] transition-colors">
                  <MapPin size={11} weight="fill" /> {city} ({list.length})
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-6 py-14 space-y-16">
        {loaded && byCity.length === 0 && <p className="text-[#1A1A1A]/50 text-sm">Nos premières réalisations arrivent bientôt.</p>}
        {byCity.map(([city, list]) => (
          <section key={city} id={slugify(city)} className="scroll-mt-28" data-testid={`city-section-${slugify(city)}`}>
            <h2 className="font-heading text-xl sm:text-2xl text-[#1A1A1A] mb-6 flex items-center gap-2">
              <MapPin size={18} weight="fill" className="text-[#D4A853]" /> {city}
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {list.map((it) => <RealisationCard key={it.id} item={it} />)}
            </div>
          </section>
        ))}
      </div>
      <div className="text-center pb-20">
        <Link to="/property-services/automobile" data-testid="realisations-cta"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors">
          Demander mon intervention <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
